import { useEffect, useRef, useState } from "react";
import NavbarAdmin from "../components/NavbarAdmin";
import "./AdminRooms.css";
import { useLanguage } from "../context/LanguageContext";
import { t } from "../i18n";
import { formatBeds } from "../utils/beds";

const BASE_URL = process.env.REACT_APP_API_URL || "";

// ส่ง token จาก localStorage แบบเดียวกับหน้า admin อื่น ๆ (backend อ่านจาก Authorization header)
// หมายเหตุ: ห้ามใส่ Content-Type เองตอนส่ง FormData — เบราว์เซอร์ตั้ง boundary ให้เอง
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PREFIXES = ["A","B","C","D","E","F","G","H"];

const ROOM_TYPES = ["Single", "Double", "Family", "Triple", "Quad"];

// ขนาดเตียงมาตรฐาน (ฟุต) — ตรงกับที่เกสต์เฮาส์ใช้จริง
const BED_SIZES = [3, 3.5, 5, 6];

const TYPE_BADGE = {
  Single: { bg: "#e0f2fe", color: "#0369a1" },
  Double: { bg: "#dcfce7", color: "#166534" },
  Family: { bg: "#ffedd5", color: "#9a3412" },
  Triple: { bg: "#f3e8ff", color: "#6b21a8" },
  Quad:   { bg: "#fce7f3", color: "#9d174d" },
};

const emptyRoom = {
  name: "",
  nameEn: "",
  description: "",
  breakfast: true,
  adults: 1,
  children: 0,
  nights: "",
  type: "Double",
  price: 0,
  quantity: 1,
  prefix: "A"
};

function AdminRooms() {
  const { lang } = useLanguage();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyRoom);
  const [imageFile, setImageFile] = useState(null);
  const [extraFiles, setExtraFiles] = useState([]);        // รูปเพิ่มเติมใหม่ที่เพิ่งเลือก (File[])
  const [existingImages, setExistingImages] = useState([]); // รูปเพิ่มเติมเดิม (filename[]) ตอนแก้ไข
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  // รายการยูนิตแบบ manual (แก้เลขห้องเองได้): [{ key, _id?, roomNumber, hasBooking }]
  const [manualUnitsList, setManualUnitsList] = useState([]);
  // รายการเตียง: [{ key, size, count }] — เก็บแยกจาก form เพราะ FormData แปลง array เป็น "[object Object]"
  const [bedsList, setBedsList] = useState([]);
  const [unitsOpen, setUnitsOpen] = useState(false); // เปิด/ปิดรายการยูนิต (กันยาวเกิน)
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null); // { type: "delete", room }
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { loadRooms(); }, []);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const loadRooms = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}/api/rooms`);
      const data = await res.json();
      setRooms(data);
    } catch {
      showToast(t(lang, "arToastLoadFail"), false);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadRooms();
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  // เพิ่มรูปเพิ่มเติมที่เลือก (สะสมต่อ ไม่ทับของเดิม) จำกัดรวมไม่เกิน 12 รูป
  const handleExtraFiles = (e) => {
    const picked = Array.from(e.target.files || []);
    setExtraFiles(prev => {
      const room = 12 - existingImages.length - prev.length;
      return [...prev, ...picked.slice(0, Math.max(0, room))];
    });
    e.target.value = ""; // เคลียร์เพื่อเลือกไฟล์ชื่อเดิมซ้ำได้
  };

  const removeExtraFile = (idx) =>
    setExtraFiles(prev => prev.filter((_, i) => i !== idx));

  const removeExistingImage = (name) =>
    setExistingImages(prev => prev.filter(n => n !== name));

  /* ── ยูนิตแบบ manual: โหลด / เพิ่ม / แก้ / ลบ ── */
  let unitKeySeq = 0;
  const loadManualUnits = async (roomId) => {
    try {
      const [uRes, bRes] = await Promise.all([
        fetch(`${BASE_URL}/api/room-units`, { headers: getAuthHeaders() }),
        fetch(`${BASE_URL}/api/bookings`,   { headers: getAuthHeaders() }),
      ]);
      const units    = await uRes.json();
      const bookings = bRes.ok ? await bRes.json() : [];
      const activeUnitIds = new Set(
        (Array.isArray(bookings) ? bookings : [])
          .filter(b => ["Pending","Approved","Confirmed","CheckedIn"].includes(b.status) && b.roomUnit)
          .map(b => String(b.roomUnit?._id || b.roomUnit))
      );
      const list = (Array.isArray(units) ? units : [])
        .filter(u => String(u.roomType?._id || u.roomType) === String(roomId))
        .map(u => ({ key: `u${unitKeySeq++}`, _id: u._id, roomNumber: u.roomNumber || "", hasBooking: activeUnitIds.has(String(u._id)) }));
      setManualUnitsList(list);
    } catch {
      showToast(t(lang, "arUnitLoadFail"), false);
      setManualUnitsList([]);
    }
  };
  // สลับโหมดตอนสร้างห้องใหม่: อัตโนมัติ (prefix+จำนวน) หรือ กำหนดเลขเอง
  const setUnitMode = (manual) => {
    setForm(f => ({ ...f, manualUnits: manual }));
    if (manual) {
      setUnitsOpen(true);
      setManualUnitsList(prev => prev.length ? prev : [{ key: `n${Date.now()}`, roomNumber: "", hasBooking: false }]);
    } else {
      setManualUnitsList([]);
    }
  };
  const addManualUnit    = () => setManualUnitsList(prev => [...prev, { key: `n${Date.now()}`, roomNumber: "", hasBooking: false }]);
  const changeManualUnit = (key, val) => setManualUnitsList(prev => prev.map(u => u.key === key ? { ...u, roomNumber: val } : u));
  const removeManualUnit = (key) => setManualUnitsList(prev => prev.filter(u => u.key !== key));

  /* ── เตียง: เพิ่ม / แก้ / ลบ ── */
  // ใช้ counter แทน Date.now() — กดเพิ่มรัว ๆ ใน ms เดียวกันจะได้ key ไม่ชนกัน
  const bedKeySeq = useRef(0);
  const newBedRow = (size = 3.5, count = 1) => ({ key: `b${++bedKeySeq.current}`, size, count });
  const addBed    = () => setBedsList(prev => [...prev, newBedRow()]);
  const changeBed = (key, field, val) => setBedsList(prev => prev.map(b => b.key === key ? { ...b, [field]: val } : b));
  const removeBed = (key) => setBedsList(prev => prev.filter(b => b.key !== key));
  // ตัดแถวที่กรอกไม่ครบทิ้งก่อนส่ง/ก่อนแสดง preview
  const cleanBeds = () => bedsList
    .map(b => ({ size: Number(b.size), count: Number(b.count) }))
    .filter(b => b.size > 0 && b.count > 0);

  const submit = async () => {
    // ห้อง manual ต้องมีเลขห้องอย่างน้อย 1 ห้อง (กันสร้างห้องที่ไม่มียูนิต)
    if (form.manualUnits && !manualUnitsList.some(u => String(u.roomNumber).trim())) {
      showToast(t(lang, "arUnitNeedOne"), false);
      return;
    }

    const formData = new FormData();
    // หมายเหตุ: FormData.append แปลงทุกค่าเป็น string — array/object จะกลายเป็น "[object Object]"
    // ฟิลด์ที่เป็น array ต้องส่งเป็น JSON แยก (แบบเดียวกับ existingImages ข้างล่าง)
    Object.keys(form).forEach(k => formData.append(k, form[k]));
    formData.append("beds", JSON.stringify(cleanBeds())); // ส่งเสมอแม้เป็น [] เพื่อให้ลบเตียงได้
    if (imageFile) formData.append("image", imageFile);
    extraFiles.forEach(f => formData.append("images", f));
    // แจ้ง backend ว่ารูปเดิมไหนเก็บไว้บ้าง (ส่งเสมอเมื่อแก้ไข เพื่อรองรับการลบ)
    if (editingId) formData.append("existingImages", JSON.stringify(existingImages));

    const method = editingId ? "PUT" : "POST";
    const url = editingId
      ? `${BASE_URL}/api/rooms/${editingId}`
      : `${BASE_URL}/api/rooms`;

    try {
      const res = await fetch(url, { method, body: formData, credentials: "include", headers: getAuthHeaders() });
      if (!res.ok) {
        // ดึงข้อความ error จริงจาก server (401 = login หมดอายุ, 403 = ไม่ใช่ admin ฯลฯ)
        let msg = `HTTP ${res.status}`;
        try { const j = await res.json(); if (j?.message) msg = j.message; } catch {}
        showToast(`${t(lang, "arToastSaveFail")} (${msg})`, false);
        return;
      }

      // ห้อง manual: บันทึกเลขห้อง (เพิ่ม/แก้/ลบ) แยกอีก endpoint — รองรับทั้งสร้างใหม่และแก้ไข
      if (form.manualUnits) {
        const saved  = await res.json().catch(() => ({}));
        const roomId = editingId || saved.room?._id;
        if (roomId) {
          const uRes = await fetch(`${BASE_URL}/api/rooms/${roomId}/units`, {
            method: "PUT",
            headers: { "Content-Type": "application/json", ...getAuthHeaders() },
            body: JSON.stringify({ units: manualUnitsList.map(u => ({ _id: u._id, roomNumber: u.roomNumber })) }),
          });
          const uData = await uRes.json().catch(() => ({}));
          if (!uRes.ok) { showToast(uData.message || t(lang, "arToastSaveFail"), false); return; }
          if (uData.skipped?.length) showToast(uData.message, true);
        }
      }

      showToast(editingId ? t(lang, "arToastEditOk") : t(lang, "arToastAddOk"));
      setForm(emptyRoom);
      setManualUnitsList([]);
      setBedsList([]);
      setImageFile(null);
      setExtraFiles([]);
      setExistingImages([]);
      setEditingId(null);
      setShowForm(false);
      loadRooms();
    } catch {
      showToast(t(lang, "arToastSaveFail"), false);
    }
  };

  const editRoom = (room) => {
    setForm({
      name: room.name,
      nameEn: room.nameEn || "",
      description: room.description,
      breakfast: room.breakfast,
      adults: room.adults,
      children: room.children,
      nights: room.nights,
      type: room.type,
      price: room.price,
      quantity: room.quantity || 1,
      prefix: room.prefix || "A",
      manualUnits: !!room.manualUnits
    });
    setBedsList((room.beds || []).map(b => newBedRow(b.size, b.count)));
    setImageFile(null);
    setExtraFiles([]);
    setExistingImages(room.images || []);
    setEditingId(room._id);
    setManualUnitsList([]);
    setUnitsOpen(false);
    if (room.manualUnits) loadManualUnits(room._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const confirmDelete = async () => {
    if (!modal || actionLoading) return;
    setActionLoading(true);
    try {
      await fetch(`${BASE_URL}/api/rooms/${modal.room._id}`, { method: "DELETE", credentials: "include", headers: getAuthHeaders() });
      setRooms(prev => prev.filter(r => r._id !== modal.room._id));
      showToast(`${t(lang,"arToastDeleteOk")} "${modal.room.name}"`);
    } catch {
      showToast(t(lang, "arToastSaveFail"), false);
    } finally {
      setActionLoading(false);
      setModal(null);
    }
  };

  const previewNumbers = () => {
    const qty = Number(form.quantity) || 0;
    if (qty <= 0) return "";
    const nums = Array.from({ length: Math.min(qty, 4) }, (_, i) => `${form.prefix}${101 + i}`);
    if (qty > 4) nums.push(`… ${form.prefix}${100 + qty}`);
    return nums.join(", ");
  };

  // Filter
  const counts = {
    all: rooms.length,
    Single: rooms.filter(r => r.type === "Single").length,
    Double: rooms.filter(r => r.type === "Double").length,
    Family: rooms.filter(r => r.type === "Family").length,
    Triple: rooms.filter(r => r.type === "Triple").length,
    Quad:   rooms.filter(r => r.type === "Quad").length,
  };

  const filtered = rooms.filter(r => {
    const q = search.toLowerCase();
    const matchQ = r.name?.toLowerCase().includes(q) || r.description?.toLowerCase().includes(q);
    if (!matchQ) return false;
    if (filter === "all") return true;
    return r.type === filter;
  });

  return (
    <>
      <NavbarAdmin />

      {/* Toast */}
      {toast && (
        <div className={`ar-toast ${toast.ok ? "ok" : "err"}`}>
          {toast.ok ? "✓" : "✕"} {toast.msg}
        </div>
      )}

      {/* Delete Modal */}
      {modal && (
        <div className="ar-modal-bg" onClick={(e) => e.target === e.currentTarget && setModal(null)}>
          <div className="ar-modal">
            <div className="ar-modal-icon">🗑️</div>
            <div className="ar-modal-title">{t(lang, "arDeleteConfirm")}</div>
            <div className="ar-modal-desc">
              {t(lang, "arDeleteDesc")} <strong>"{modal.room.name}"</strong>?
              <br /><span style={{ color:"#ef4444", fontSize:12 }}>{t(lang, "arDeleteWarn")}</span>
            </div>
            <div className="ar-modal-btns">
              <button className="ar-mbtn-cancel" onClick={() => setModal(null)} disabled={actionLoading}>{t(lang, "arCancelBtn")}</button>
              <button className="ar-mbtn-del" onClick={confirmDelete} disabled={actionLoading}>
                {actionLoading ? t(lang, "arDeleting") : t(lang, "arDeleteBtn")}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="ar-wrap">
        <div className="ar-page">

          {/* ── FORM PANEL ── */}
          {showForm && (
            <div className="ar-form-panel">
              <div className="ar-form-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  {editingId
                    ? <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></>
                    : <><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></>
                  }
                </svg>
                {editingId ? t(lang, "arEditRoom") : t(lang, "arAddNewRoom")}
              </div>

              {/* ข้อมูลพื้นฐาน */}
              <div className="ar-section">
                <h4>{t(lang, "arBasicInfo")}</h4>
                <div className="ar-grid-2" style={{ marginBottom:14 }}>
                  <div>
                    <label className="ar-label">{t(lang, "arRoomNameTh")}</label>
                    <input className="ar-input" name="name" placeholder={t(lang, "arRoomNameThPh")} value={form.name} onChange={handleChange} />
                  </div>
                  <div>
                    <label className="ar-label">{t(lang, "arRoomType")}</label>
                    <select className="ar-select" name="type" value={form.type} onChange={handleChange}>
                      {ROOM_TYPES.map(rt => <option key={rt} value={rt}>{rt}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{ marginBottom:14 }}>
                  <label className="ar-label">
                    {t(lang, "arRoomNameEn")}
                    <span style={{ fontWeight:400, color:"#6b7280", fontSize:12, marginLeft:8 }}>
                      {t(lang, "arRoomNameEnNote")}
                    </span>
                  </label>
                  <input
                    className="ar-input"
                    name="nameEn"
                    placeholder="e.g. Double Room"
                    value={form.nameEn}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <label className="ar-label">{t(lang, "arRoomDesc")}</label>
                  <textarea className="ar-textarea" name="description" placeholder={t(lang, "arRoomDescPh")} value={form.description} onChange={handleChange} />
                </div>
              </div>

              {/* ความจุ */}
              <div className="ar-section">
                <h4>{t(lang, "arCapacity")}</h4>
                <div className="ar-grid-3">
                  <div>
                    <label className="ar-label">{t(lang, "arMaxAdults")}</label>
                    <select className="ar-select" name="adults" value={form.adults} onChange={handleChange}>
                      {[1,2,3,4].map(n => <option key={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="ar-label">{t(lang, "arMaxChildren")}</label>
                    <select className="ar-select" name="children" value={form.children} onChange={handleChange}>
                      {[0,1,2,3].map(n => <option key={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="ar-label">{t(lang, "arDefaultNights")}</label>
                    <input className="ar-input" name="nights" placeholder={t(lang, "arDefaultNightsPh")} value={form.nights} onChange={handleChange} />
                  </div>
                </div>

                {/* เตียงในห้อง — ขนาด (ฟุต) × จำนวน มีได้หลายขนาดในห้องเดียว */}
                <div style={{ marginTop: 16 }}>
                  <label className="ar-label">
                    {t(lang, "arBeds")}
                    <span style={{ fontWeight:400, color:"#6b7280", fontSize:12, marginLeft:8 }}>
                      {t(lang, "arBedsNote")}
                    </span>
                  </label>

                  {bedsList.length === 0 && (
                    <p className="ar-preview-text" style={{ marginTop: 0 }}>{t(lang, "arBedsEmpty")}</p>
                  )}

                  <div className="ar-unit-list" style={{ maxHeight: "none" }}>
                    {bedsList.map((b, i) => (
                      <div className="ar-unit-row" key={b.key}>
                        <span className="ar-unit-idx">{i + 1}</span>
                        <select
                          className="ar-select"
                          style={{ maxWidth: 140 }}
                          value={b.size}
                          onChange={e => changeBed(b.key, "size", Number(e.target.value))}
                        >
                          {BED_SIZES.map(s => (
                            <option key={s} value={s}>{s} {t(lang, "arBedFt")}</option>
                          ))}
                        </select>
                        <span style={{ color:"#9ca3af", fontSize:13 }}>×</span>
                        <input
                          className="ar-input"
                          type="number"
                          min="1"
                          max="20"
                          style={{ width: 78, flex: "0 0 auto" }}
                          value={b.count}
                          onChange={e => changeBed(b.key, "count", e.target.value)}
                        />
                        <span style={{ color:"#6b7280", fontSize:13 }}>{t(lang, "arBedUnit")}</span>
                        <button type="button" className="ar-unit-del" onClick={() => removeBed(b.key)}>✕</button>
                      </div>
                    ))}
                  </div>

                  <button type="button" className="ar-unit-add" onClick={addBed}>
                    {t(lang, "arAddBed")}
                  </button>

                  {/* preview เรียก formatBeds ตัวเดียวกับหน้าลูกค้า — เห็นตรงกันเป๊ะ */}
                  {cleanBeds().length > 0 && (
                    <p className="ar-preview-text">
                      🛏 {t(lang, "arBedsPreview")} <strong>{formatBeds(lang, cleanBeds())}</strong>
                    </p>
                  )}
                </div>
              </div>

              {/* จำนวนยูนิต */}
              <div className="ar-section">
                <h4>{t(lang, "arUnits")}</h4>
                {!editingId && (
                  <div className="ar-unit-mode">
                    <button type="button" className={`ar-unit-mode-btn${!form.manualUnits ? " active" : ""}`} onClick={() => setUnitMode(false)}>
                      {t(lang, "arUnitModeAuto")}
                    </button>
                    <button type="button" className={`ar-unit-mode-btn${form.manualUnits ? " active" : ""}`} onClick={() => setUnitMode(true)}>
                      {t(lang, "arUnitModeManual")}
                    </button>
                  </div>
                )}
                {form.manualUnits ? (
                  /* ห้องเลขกำหนดเองตามผังจริง — แก้เลข/เพิ่ม/ลบ ทีละห้องได้ */
                  <div className="ar-manual-units">
                    <p className="ar-preview-text" style={{ marginTop: 0 }}>{t(lang, "arManualEditHint")}</p>
                    <button type="button" className="ar-unit-toggle" onClick={() => setUnitsOpen(o => !o)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                        style={{ transition: "transform .2s", transform: unitsOpen ? "rotate(180deg)" : "none" }}>
                        <polyline points="6 9 12 15 18 9"/>
                      </svg>
                      {unitsOpen ? t(lang, "arUnitsHide") : t(lang, "arUnitsShow")} ({manualUnitsList.length})
                    </button>
                    {unitsOpen && (
                      <>
                        <div className="ar-unit-list">
                          {manualUnitsList.map((u, i) => (
                            <div className="ar-unit-row" key={u.key}>
                              <span className="ar-unit-idx">{i + 1}</span>
                              <input
                                className="ar-input ar-unit-input"
                                value={u.roomNumber}
                                placeholder={t(lang, "arUnitNumberPh")}
                                onChange={e => changeManualUnit(u.key, e.target.value)}
                              />
                              {u.hasBooking && <span className="ar-unit-booked">{t(lang, "arUnitBooked")}</span>}
                              <button
                                type="button"
                                className="ar-unit-del"
                                title={u.hasBooking ? t(lang, "arUnitBooked") : ""}
                                disabled={u.hasBooking}
                                onClick={() => removeManualUnit(u.key)}
                              >✕</button>
                            </div>
                          ))}
                        </div>
                        <button type="button" className="ar-unit-add" onClick={addManualUnit}>
                          {t(lang, "arAddUnit")}
                        </button>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="ar-grid-2">
                      <div>
                        <label className="ar-label">{t(lang, "arUnitPrefix")}</label>
                        <select className="ar-select" name="prefix" value={form.prefix} onChange={handleChange}>
                          {PREFIXES.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="ar-label">{t(lang, "arUnitCount")}</label>
                        <input className="ar-input" type="number" name="quantity" min="1" max="100" value={form.quantity} onChange={handleChange} />
                      </div>
                    </div>
                    {Number(form.quantity) > 0 && (
                      <p className="ar-preview-text">
                        {t(lang, "arRoomNumbers")} <strong>{previewNumbers()}</strong>
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* ราคา */}
              <div className="ar-section">
                <h4>{t(lang, "arPricing")}</h4>
                <div>
                  <label className="ar-label">{t(lang, "arRoomPrice")}</label>
                  <input className="ar-input" type="number" name="price" value={form.price} onChange={handleChange} />
                </div>
              </div>

              {/* ตัวเลือกเพิ่มเติม */}
              <div className="ar-section">
                <h4>{t(lang, "arOptions")}</h4>
                <label className="ar-checkbox-label" style={{ marginBottom:14 }}>
                  <input type="checkbox" name="breakfast" checked={form.breakfast} onChange={handleChange} />
                  {t(lang, "arBreakfast")}
                </label>
                <label className="ar-label">{t(lang, "arPhoto")}</label>
                <input className="ar-file-input" type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files[0])} />

                {/* รูปเพิ่มเติม (แกลเลอรี) */}
                <label className="ar-label" style={{ marginTop:14 }}>
                  {t(lang, "arPhotoExtra")}
                  <span style={{ fontWeight:400, color:"#6b7280", fontSize:12, marginLeft:8 }}>
                    {t(lang, "arPhotoExtraNote")}
                  </span>
                </label>
                <input
                  className="ar-file-input"
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={existingImages.length + extraFiles.length >= 12}
                  onChange={handleExtraFiles}
                />

                {(existingImages.length > 0 || extraFiles.length > 0) && (
                  <div className="ar-thumb-row">
                    {existingImages.map(name => (
                      <div className="ar-thumb" key={`old-${name}`}>
                        <img src={`${BASE_URL}/uploads/${name}`} alt="" />
                        <button type="button" className="ar-thumb-x"
                          title={t(lang, "arPhotoRemove")} onClick={() => removeExistingImage(name)}>✕</button>
                      </div>
                    ))}
                    {extraFiles.map((f, i) => (
                      <div className="ar-thumb" key={`new-${i}`}>
                        <img src={URL.createObjectURL(f)} alt="" />
                        <button type="button" className="ar-thumb-x"
                          title={t(lang, "arPhotoRemove")} onClick={() => removeExtraFile(i)}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="ar-form-actions">
                <button className="ar-btn-submit" onClick={submit}>
                  {editingId ? t(lang, "arSaveEdit") : t(lang, "arAddRoomBtn")}
                </button>
                <button className="ar-btn-cancel" onClick={() => { setShowForm(false); setEditingId(null); setForm(emptyRoom); setImageFile(null); setExtraFiles([]); setExistingImages([]); setManualUnitsList([]); setBedsList([]); }}>
                  {t(lang, "arCancelBtn")}
                </button>
              </div>
            </div>
          )}

          {/* ── ROOM LIST CARD ── */}
          <div className="ar-card">

            {/* Header */}
            <div className="ar-hdr">
              <div className="ar-title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                {t(lang, "arTitle")}
                <span className="ar-badge-count">{rooms.length} {t(lang, "arRooms")}</span>
              </div>

              <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap" }}>
                <button className={`ar-btn-refresh ${refreshing ? "spinning" : ""}`} onClick={handleRefresh} disabled={loading}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                  </svg>
                  {t(lang, "arRefresh")}
                </button>

                <div className="ar-search-wrap">
                  <svg className="ar-search-ico" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input type="text" placeholder={t(lang, "arSearch")} value={search} onChange={(e) => setSearch(e.target.value)} />
                  {search && <button className="ar-search-clear" onClick={() => setSearch("")}>✕</button>}
                </div>

                <button
                  className="ar-btn-add"
                  onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyRoom); setImageFile(null); setExtraFiles([]); setExistingImages([]); setManualUnitsList([]); setBedsList([newBedRow()]); window.scrollTo({ top:0, behavior:"smooth" }); }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  {t(lang, "arAddRoom")}
                </button>
              </div>
            </div>

            {/* Filter Pills */}
            <div className="ar-pills">
              {[
                { key:"all",    label:t(lang, "arAll"), n:counts.all },
                { key:"Single", label:"Single",  n:counts.Single },
                { key:"Double", label:"Double",  n:counts.Double },
                { key:"Family", label:"Family",  n:counts.Family },
                { key:"Triple", label:"Triple",  n:counts.Triple },
                { key:"Quad",   label:"Quad",    n:counts.Quad },
              ].map(p => (
                <button
                  key={p.key}
                  className={`ar-pill ${filter === p.key ? "active" : ""}`}
                  onClick={() => setFilter(p.key)}
                >
                  {p.label}
                  <span className="ar-pill-n">{p.n}</span>
                </button>
              ))}
              {search && (
                <span style={{ fontSize:12, color:"#888", marginLeft:4, alignSelf:"center" }}>
                  {filtered.length}
                </span>
              )}
            </div>

            {/* Skeleton */}
            {loading && Array.from({ length: 4 }).map((_, i) => (
              <div className="ar-skeleton" key={i}>
                <div className="ar-skel-img" />
                <div className="ar-skel-lines">
                  <div className="ar-skel-line w60" />
                  <div className="ar-skel-line w40" />
                  <div className="ar-skel-line w30" />
                </div>
              </div>
            ))}

            {/* Empty */}
            {!loading && filtered.length === 0 && (
              <div className="ar-state">
                <div className="ar-state-icon">🏨</div>
                <div className="ar-state-msg">
                  {search ? t(lang, "arNoSearch") : t(lang, "arNoRooms")}
                </div>
              </div>
            )}

            {/* Room List */}
            {!loading && filtered.map(room => {
              const badge = TYPE_BADGE[room.type] || TYPE_BADGE.Double;
              return (
                <div className="ar-room-card" key={room._id}>
                  {/* Image */}
                  {room.image
                    ? <img className="ar-room-img" src={`${BASE_URL}/uploads/${room.image}`} alt={room.name} />
                    : <div className="ar-room-img-placeholder">🛏️</div>
                  }

                  {/* Info */}
                  <div className="ar-info">
                    <div className="ar-name">
                      {room.name}
                      <span className="tbadge" style={{ background:badge.bg, color:badge.color }}>
                        {room.type}
                      </span>
                      {room.breakfast && (
                        <span className="tbadge" style={{ background:"#fef9c3", color:"#854d0e" }}>
                          {t(lang, "arBreakfastBadge")}
                        </span>
                      )}
                    </div>
                    <div className="ar-desc">{room.description || "—"}</div>
                    <div className="ar-meta">
                      <span>{t(lang, "arAdults")} {room.adults} {t(lang, "arPerson")}</span>
                      {room.children > 0 && (
                        <><span className="ar-meta-dot">•</span><span>{t(lang, "arChildren")} {room.children} {t(lang, "arPerson")}</span></>
                      )}
                      {room.nights && (
                        <><span className="ar-meta-dot">•</span><span>🌙 {room.nights}</span></>
                      )}
                      <span className="ar-meta-dot">•</span>
                      <span>
                        🏨 {room.quantity || 1} {t(lang, "arUnitsLabel")}
                        {!room.manualUnits && ` · ${room.prefix || "A"}101–${room.prefix || "A"}${100 + (room.quantity || 1)}`}
                      </span>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="ar-price">
                    ฿{Number(room.price).toLocaleString()}
                    <div className="ar-price-sub">{t(lang, "arPerNight")}</div>
                  </div>

                  {/* Actions */}
                  <div className="ar-actions">
                    <button className="ar-btn ar-btn-edit" onClick={() => editRoom(room)}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                      </svg>
                      {t(lang, "arEdit")}
                    </button>
                    <button className="ar-btn ar-btn-del" onClick={() => setModal({ type:"delete", room })}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6"/><path d="M14 11v6"/>
                        <path d="M9 6V4h6v2"/>
                      </svg>
                      {t(lang, "arDelete")}
                    </button>
                  </div>
                </div>
              );
            })}

          </div>
        </div>
      </div>
    </>
  );
}

export default AdminRooms;